/**
 * Web Push reminders for the bills checklist.
 *
 * The push backend is a separate Cloudflare Worker (`worker/` in this repo)
 * with a daily cron — Pages itself can't run cron triggers. After deploying
 * it, set its workers.dev URL below (see README "Push reminders" for the
 * full one-time setup).
 *
 * Flow: user taps the bell → Notification permission (iOS 16.4+ installed
 * PWA, user gesture required) → pushManager.subscribe with the worker's
 * VAPID public key → subscription + bills synced to the worker → the worker
 * pushes at 8am on due days / daily while overdue.
 */

/** Base URL of the deployed push worker. */
export const PUSH_API_BASE = 'https://bills-checklist-push.cshohn773.workers.dev';

/** The subset of an Expense the worker needs to decide what to remind about. */
export interface PushBill {
  id: string;
  title: string;
  /** ISO yyyy-mm-dd; '' = no date */
  date: string;
  completed: boolean;
  /** Local alarm time "HH:MM" — default 08:00 on the worker when missing. */
  remindAt?: string;
  /** "Almost due" lead time in days (1/3/7) — default 3 on the worker when missing. */
  dueSoonDays?: number;
}

/**
 * Push is only available in production builds (the service worker — which
 * must be active to receive pushes — is only registered there) and where the
 * browser exposes the Push API (installed PWAs on iOS 16.4+ / Safari, Chrome).
 */
export function isPushSupported(): boolean {
  return (
    process.env.NODE_ENV === 'production' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** base64url (as served by the worker) → Uint8Array for pushManager.subscribe. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** VAPID application server key, fetched from the push worker. */
export async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const res = await fetch(`${PUSH_API_BASE}/api/vapid-public-key`);
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const publicKey =
      data && typeof data === 'object' && 'publicKey' in data
        ? (data as { publicKey: unknown }).publicKey
        : null;
    return typeof publicKey === 'string' ? publicKey : null;
  } catch {
    return null;
  }
}

/** Current push subscription, or null when not subscribed / unsupported. */
export async function getActiveSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Minutes EAST of UTC for the device (Hong Kong = +480). */
export function tzOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

async function post(path: string, body: unknown): Promise<boolean> {
  try {
    const res = await fetch(`${PUSH_API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Ask for permission and subscribe to push. Must be called from a user
 * gesture (iOS requires it). Returns the subscription, or null if the user
 * denied / the backend is unreachable / the browser can't subscribe.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const key = await fetchVapidPublicKey();
  if (!key) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
    await post('/api/subscribe', {
      subscription: subscription.toJSON(),
      tzOffset: tzOffsetMinutes(),
    });
    return subscription;
  } catch {
    return null;
  }
}

/** Remove the device from the backend and unsubscribe locally. */
export async function unsubscribeFromPush(subscription: PushSubscription): Promise<void> {
  await post('/api/unsubscribe', { endpoint: subscription.endpoint });
  try {
    await subscription.unsubscribe();
  } catch {
    /* already unsubscribed */
  }
}

/** Push the latest bills (current month) to the worker so reminders stay accurate. */
export function syncBills(subscription: PushSubscription, bills: PushBill[]): Promise<boolean> {
  return post('/api/sync', {
    endpoint: subscription.endpoint,
    tzOffset: tzOffsetMinutes(),
    bills,
  });
}

/** Outcome of a test-push request: sent, or why it failed. */
export type TestPushResult =
  | 'sent' // worker accepted and pushed it
  | 'resubscribe' // worker doesn't know this device (404/410) — re-enable reminders
  | 'error'; // network/server problem — the push never left the worker

/**
 * Ask the worker to send a test notification to this device right now.
 * Returns the outcome so the UI can explain what to do next (v0.12):
 * a 404/410 means the server-side subscription record is missing or dead,
 * which is the usual cause of "reminders were working, now they're not".
 */
export async function sendTestPush(subscription: PushSubscription): Promise<TestPushResult> {
  try {
    const res = await fetch(`${PUSH_API_BASE}/api/test-push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    if (res.ok) return 'sent';
    if (res.status === 404 || res.status === 410) return 'resubscribe';
    return 'error';
  } catch {
    return 'error';
  }
}
